import React from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { PAGES, ACTIONS } from "hooks/permissions/permissions";
import RequirePermission from "components/requirePermission";

// Layouts
import AuthLayout from "layouts/AuthLayout";
import AppLayout from "layouts/AppLayout";
import globalRouter from "globalRouter.ts";

// Authentication Routes
import SignIn from "./views/auth/login";
import ForgotPassword from "./views/auth/forgotPassword/index";
import SetPassword from "./views/auth/forgotPassword/setPassword";

// Admin Routes
import Dashboard from "./views/app/dashboard";
import ManpowerTrend from "./views/app/reports/manpowerTrend";
import ContractorManpower from "./views/app/reports/contractorManpower";
import MonthlyAttendance from "./views/app/reports/monthlyAttendance";
import DailyAttendance from "./views/app/reports/dailyAttendance";
import FirstInLastOut from "./views/app/reports/firstInLastOut";
import SearchEmployee from "./views/app/reports/searchEmployee";

import Alerts from "./views/app/configuration/alerts";
import Department from "./views/app/configuration/department";
import DepartmentForm from "./views/app/configuration/department/form";
import Shift from "./views/app/configuration/shift";
import ShiftForm from "./views/app/configuration/shift/form";
import Employee from "./views/app/configuration/employee";
import EmployeeForm from "./views/app/configuration/employee/form";
import BulkUpload from "./views/app/configuration/employee/bulkUpload/bulkUploadForm";
import GateWay from "./views/app/configuration/rfid";
import GateWayForm from "./views/app/configuration/rfid/form";
import Beacon from "./views/app/configuration/tag";
import BeaconForm from "./views/app/configuration/tag/form";
import Zone from "./views/app/configuration/zone";
import ZoneForm from "./views/app/configuration/zone/form";
import Admin from "./views/app/admin/admin";
import AdminForm from "./views/app/admin/admin/form";
import Roles from "./views/app/admin/roles";
import RolesForm from "./views/app/admin/roles/form";
import Profile from "./views/app/profile";

/** List pages need VIEW; `form` routes need ADD for `create`, EDIT otherwise. */
const guard = (page, element, options = {}) => (
  <RequirePermission page={page} {...options}>{element}</RequirePermission>
);

function App() {
  const navigate = useNavigate();
  globalRouter.navigate = navigate;

  return (
    <Routes>
      <Route element={<AuthLayout />}>
        <Route exact path="/" element={<SignIn />} />
        <Route path="/login" element={<SignIn />} />
        <Route path="/forgotPassword" element={<ForgotPassword />} />
        <Route path="/setPassword" element={<SetPassword />} />
      </Route>
      <Route element={<AppLayout />}>
        <Route path="/" element={guard(PAGES.DASHBOARD, <Dashboard />, { redirect: true })} />
        <Route path="/dashboard" element={guard(PAGES.DASHBOARD, <Dashboard />, { redirect: true })} />
        <Route path="/reports" element={<Navigate to="/reports/manpower-trend" />} />
        <Route path="/reports/manpower-trend" element={guard(PAGES.REPORT, <ManpowerTrend />)} />
        <Route path="/reports/contractor-manpower" element={guard(PAGES.REPORT, <ContractorManpower />)} />
        <Route path="/reports/monthly-attendance" element={guard(PAGES.REPORT, <MonthlyAttendance />)} />
        <Route path="/reports/daily-attendance" element={guard(PAGES.REPORT, <DailyAttendance />)} />
        <Route path="/reports/today-attendance" element={<Navigate to="/reports/daily-attendance" replace />} />
        <Route path="/reports/first-in-last-out" element={guard(PAGES.REPORT, <FirstInLastOut />)} />
        <Route path="/reports/search-employee" element={guard(PAGES.REPORT, <SearchEmployee />)} />
        <Route path="/reports/today-absentees" element={<Navigate to="/reports/daily-attendance" replace />} />
        <Route path="/configuration" element={<Navigate to="/configuration/employee" />} />
        <Route path="/configuration/alerts" element={guard(PAGES.ALERT, <Alerts />)} />
        <Route path="/configuration/department" element={guard(PAGES.CONTRACTOR, <Department />)} />
        <Route path="/configuration/department/:id" element={guard(PAGES.CONTRACTOR, <DepartmentForm />, { form: true })} />
        <Route path="/configuration/shift" element={guard(PAGES.SHIFT, <Shift />)} />
        <Route path="/configuration/shift/:id" element={guard(PAGES.SHIFT, <ShiftForm />, { form: true })} />
        <Route path="/configuration/employee" element={guard(PAGES.EMPLOYEE, <Employee />)} />
        <Route path="/configuration/employee/:id" element={guard(PAGES.EMPLOYEE, <EmployeeForm />, { form: true })} />
        <Route path="/configuration/employee/bulk-upload" element={guard(PAGES.EMPLOYEE, <BulkUpload />, { action: ACTIONS.ADD })} />
        <Route path="/configuration/rfid" element={guard(PAGES.RFID_READER, <GateWay />)} />
        <Route path="/configuration/rfid/:id" element={guard(PAGES.RFID_READER, <GateWayForm />, { form: true })} />
        <Route path="/configuration/tag" element={guard(PAGES.RFID_CARD, <Beacon />)} />
        <Route path="/configuration/tag/:id" element={guard(PAGES.RFID_CARD, <BeaconForm />, { form: true })} />
        <Route path="/configuration/zone" element={guard(PAGES.SITE, <Zone />)} />
        <Route path="/configuration/zone/:id" element={guard(PAGES.SITE, <ZoneForm />, { form: true })} />
        <Route path="/admin" element={guard(PAGES.USER, <Admin />)} />
        <Route path="/admin/:id" element={guard(PAGES.USER, <AdminForm />, { form: true })} />
        <Route path="/admin/roles" element={guard(PAGES.ROLES, <Roles />)} />
        <Route path="/admin/roles/:id" element={guard(PAGES.ROLES, <RolesForm />, { form: true })} />
        <Route path="/profile" element={guard(PAGES.PROFILE, <Profile />)} />
      </Route>
    </Routes>
  );
}

export default App;
